import Heading from '../Heading/Heading';
import ProgressBar from '../Progress/Bar';
import DoctorFactsCard, { DoctorFactsCardProps } from './DoctorFactsCard';
import styles from './doctor-facts.module.scss';
import classNames from 'classnames';
import HTMLReactParser from 'html-react-parser';
import ProgressRing from '../Progress/Ring';
import { Button } from '@get-mosh/msh-shared';
import { ReactElement } from 'react';

export interface DoctorFactsProps {
    title?: string;
    doctor: DoctorFactsCardProps;
    percentage?: PercentageProps;
    box1?: DoctorFactsBoxProps;
    box2?: DoctorFactsBoxProps;
    box3?: DoctorFactsBoxProps;
    box4?: DoctorFactsBoxProps;
    ctaBox: {
        text: string | JSX.Element;
        button: {
            url: string;
            text: string;
        };
    };
    references?: ReferenceItemProps[];
}

type ReferenceItemProps = {
    text: string;
    url?: string;
    target?: string;
};
type PercentageProps = {
    value: number;
    description: string | JSX.Element;
    className?: string;
    fromPrismic?: boolean;
};
const DoctorFacts = (props: DoctorFactsProps) => {
    return (
        <div className="container">
            <Heading level="h2" centered={true}>
                {props.title}
            </Heading>
            <DoctorFactsCard
                image={props.doctor.image}
                name={props.doctor.name}
                title={props.doctor.title}
                title2={props.doctor.title2}
                description={props.doctor.description}
            />
            {props.percentage && <Percentage {...props.percentage} />}
            <div className={classNames(styles['boxes'])}>
                <div className="flex flex-wrap md:flex-nowrap md:space-x-4">
                    {props.box1 && (
                        <DoctorFactsBox
                            {...props.box1}
                            className={styles['box1']}
                        />
                    )}
                    {props.box2 && (
                        <DoctorFactsBox
                            {...props.box2}
                            className={styles['box2']}
                        />
                    )}
                </div>
                {props.box3 && (
                    <DoctorFactsBox
                        {...props.box3}
                        className={styles['box3']}
                    />
                )}
                <div className="flex flex-wrap md:flex-nowrap md:space-x-4">
                    {props.box4 && (
                        <DoctorFactsBox
                            {...props.box4}
                            className={styles['box4']}
                        />
                    )}
                    {props.ctaBox && (
                        <div className={styles['cta-box']}>
                            {typeof props?.ctaBox?.text === 'string' ? (
                                <p>{HTMLReactParser(props.ctaBox.text)}</p>
                            ) : props.ctaBox.text ? (
                                props.ctaBox.text
                            ) : null}
                            <Button
                                as="a"
                                href={props.ctaBox.button.url}
                                text={props.ctaBox.button.text}
                                type={'link'}
                                stretch
                            />
                        </div>
                    )}
                </div>
            </div>
            {props.references && (
                <ol className={styles['references']}>
                    {props.references.map(
                        (e: ReferenceItemProps, i: number) => (
                            <li key={i}>
                                {`[${i + 1}] `}
                                {e.url ? (
                                    <a
                                        href={e.url}
                                        title={e.text}
                                        target={
                                            e?.target ? e.target : undefined
                                        }
                                    >
                                        {e.text}
                                    </a>
                                ) : (
                                    <>{e.text}</>
                                )}
                            </li>
                        ),
                    )}
                </ol>
            )}
        </div>
    );
};

const Percentage = (props: PercentageProps) => {
    return (
        <div className={classNames(styles['box'], styles['percentage'])}>
            <ProgressBar
                progress={props.value}
                showText={true}
                className={styles['progress-bar']}
            />
            {typeof props?.description === 'string'
                ? HTMLReactParser(props.description)
                : props.description
                    ? props.description
                    : null}
        </div>
    );
};

export type DoctorFactsBoxProps = {
    title: string;
    description?: string | JSX.Element;
    percentageBox?: PercentageBoxItem[];
    className?: string;
    mixedBox?: MixedBoxItem[];
    disabled?: boolean;
};

type MixedBoxItem = {
    type: string; // 'PercentageBox' | 'StatBox'
    stat?: number[]; // This value should be represented in a pair of numbers, e.g. [2, 6], which means: "2 in every 6".
    value?: number;
    description: string;
};

type PercentageBoxItem = {
    value: number;
    description: string | JSX.Element;
};

export const DoctorFactsBox = (props: DoctorFactsBoxProps) =>
    props?.disabled ? null : (
        <div
            className={classNames(
                styles['box'],
                styles['box-content'],
                props.className,
            )}
        >
            <Heading alternative={true}>{props.title}</Heading>
            {props.percentageBox ? (
                <div className={styles['box-percentages']}>
                    {props.percentageBox.map(
                        (
                            e: {
                                value: number;
                                description: string | JSX.Element;
                            },
                            i: number,
                        ) => (
                            <div key={i}>
                                <ProgressRing
                                    className={styles['ring']}
                                    progress={e.value}
                                    stroke={5}
                                    radius={55}
                                />
                                {typeof e?.description === 'string' ? (
                                    <p>{HTMLReactParser(e.description)}</p>
                                ) : e.description ? (
                                    e.description
                                ) : null}
                            </div>
                        ),
                    )}
                </div>
            ) : props.mixedBox ? (
                <div className={styles['box-mixed']}>
                    {props.mixedBox.map((item: MixedBoxItem, i: number) => (
                        <div className={styles['box-mixed--item']} key={i}>
                            {item.type === 'PercentageBox' && item.value && (
                                <>
                                    <ProgressRing
                                        className={styles['ring']}
                                        progress={item.value || 0}
                                        stroke={5}
                                        radius={55}
                                    />
                                    <p>{item.description}</p>
                                </>
                            )}
                            {item.type === 'StatBox' && item.stat && (
                                <>
                                    <StatBoxItem stat={item.stat} />
                                    <p>{item.description}</p>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            ) : typeof props?.description === 'string' ? (
                <p>{HTMLReactParser(props.description)}</p>
            ) : props.description ? (
                props.description
            ) : null}
        </div>
    );

type StatBoxItemProps = {
    stat: number[];
};

const StatBoxItem = ({ stat }: StatBoxItemProps) => {
    const Stats: Array<ReactElement> = [];
    if (stat[0] > 0 && stat[1] > 0) {
        for (let i = 0; i < stat[1]; i++) {
            Stats.push(
                <span key={i}>
                    <svg
                        width="26"
                        height="56"
                        viewBox="0 0 26 56"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M19.2985 24.7059C23.2951 22.4581 26 18.1375 26 13.1765C26 5.89931 20.1796 0 13 0C5.8204 0 0 5.89931 0 13.1765C0 18.1375 2.7049 22.4581 6.70154 24.7059C2.7049 26.9537 0 31.2743 0 36.2353V51.3142C0 53.9021 2.06973 56 4.62308 56H21.3769C23.9303 56 26 53.9021 26 51.3142V36.2353C26 31.2743 23.2951 26.9537 19.2985 24.7059Z"
                            fill={stat[0] > i ? '#82C780' : '#DAEED9'}
                        />
                    </svg>
                </span>,
            );
        }
    }
    return <div className={styles['stat-box']}>{Stats}</div>;
};

export default DoctorFacts;

DoctorFacts.defaultProps = {
    title: 'The truth about erectile dysfunction',
    doctor: {
        image: {
            src: '/images/doctors/christopher-love-860.jpg',
            width: 860,
            height: 860,
        },
        name: 'Dr Christopher Love',
        title: 'Urologic and Prosthetic Surgeon',
        title2: 'MBBS, FRACS',
        description:
            '"ED is a common and treatable condition that can be caused by a variety of factors. Here\'s what you need to know."',
    },
    percentage: {
        value: 94,
        description:
            '<p>of blokes in <span class="underline font-demi">this study</span> said their sex improved after taking ED meds.</p>',
    },
    box1: {
        title: 'What is ED?',
        description: `A man who's unable to get or keep an erection firm enough for sexual activity with penetration.`,
    },
    box2: {
        title: 'What causes it?',
        description: `Most ED cases have a physical cause, such as a blood vessel or heart condition, but it's common for psychological and lifestyle factors to play a role too.`,
    },
    box3: {
        title: 'How common is it?',
        percentageBox: [
            {
                value: 40,
                description: 'ED affects 40% of men at age 40.',
            },
            {
                value: 70,
                description: 'Nearly 70% of men at age 70. [1]',
            },
        ],
    },
    box4: {
        title: 'What can I do about it?',
        description: `Oral prescription medicines are available. This includes the "Blue Pill" and "Yellow Pill" which are effective for the majority of men. These 'PDE5 inhibitors’ are generally the first line of therapy, but there are other options available; just ask your Mosh doctor about them. <a href="#" title="Get started here" class="underline font-demi">Get started here</a>`,
    },
    ctaBox: {
        text: 'Want to find out more? We worked with our doctors to create this <span class="font-bolder">Ultimate Guide to ED</span>.',
        button: {
            url: '/premature-ejaculation-treatment',
            text: 'Read guide',
            style: 'dark',
        },
    },
};
